import { useCallback, useEffect, useRef, useState } from 'react';
import './BoardViewer.scss';
import DataGrid, { Toolbar, Item, SearchPanel, Column, Paging, LoadPanel as LoadPanelDg, Scrolling, Pager, GroupPanel, ColumnChooser, FilterRow, Export } from 'devextreme-react/data-grid';
import { Button, Tooltip, LoadIndicator } from 'devextreme-react';
import { useApiReports } from '../../../api/api.reports';
import { useApiOrders } from '../../../api/api.orders';
import { FormPopupSimpleItem, newToast } from '../..';
import { FormField } from '../../../types/types.system';
import { filter } from '../../../images/icons';

interface BoardViewerProps {
  board: any; // El tablero seleccionado con Rp_Id, Rp_Nombre, Rp_Query, Rp_Filtros
  onClose?: () => void; // Callback opcional para cerrar el viewer
  showCloseButton?: boolean; // Mostrar botón de cerrar
  height?: string; // Altura personalizada del grid
  onMinimize?: () => void; // Callback para minimizar el modal cuando está en modo popup
}

export const BoardViewer = ({ board, onClose, showCloseButton = false, height = 'calc(100vh - 200px)', onMinimize }: BoardViewerProps) => {
  // Los valores de los filtros viven por instancia del visor, no en el modulo. Cuando este
  // objeto era global, lo que se escribia en un tablero se arrastraba al siguiente si ambos
  // tenian un filtro con el mismo 'equivalence' (Ord_FechaCreacion esta en casi todos), y lo
  // mismo pasaba entre el visor de la pagina y el del popup del encabezado.
  const dataFiltered = useRef<any>({}).current;
  const { runBoardReport } = useApiReports();
  const { findOrder } = useApiOrders();
  const [filtrosVisible, setFiltrosVisible] = useState<boolean>(false);
  const [filtros, setFiltros] = useState<FormField[]>([]);
  const [filtersApplied, setFiltersApplied] = useState<any>([]);
  const [sending, setSending] = useState<boolean>(false);
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [visibleCount, setVisibleCount] = useState<number>(0);
  const [loadingOrder, setLoadingOrder] = useState<boolean>(false);
  const gridRef = useRef<DataGrid>(null);

  // Resetear estado cuando cambia el tablero
  useEffect(() => {
    if (!board) return;

    setFilteredData([]);
    setFiltersApplied([]);

    // Se vacia conservando la referencia: el Form de DevExtreme escribe sobre este mismo
    // objeto, asi que reemplazarlo por uno nuevo desengancharia el formulario.
    Object.keys(dataFiltered).forEach((key) => delete dataFiltered[key]);

    try {
      const parsedFilters: FormField[] = JSON.parse(board.Rp_Filtros || '[]');
      setFiltros(parsedFilters);

      // Auto-abrir filtros al seleccionar un tablero nuevo
      if (parsedFilters.length > 0) {
        setFiltrosVisible(true);
      }
    } catch (e) {
      console.error('Error parsing filters:', e);
      setFiltros([]);
    }
  }, [board?.Rp_Id, dataFiltered]);

  const handleClicFilter = useCallback(() => {
    const rangosIncompletos: string[] = [];
    const obligatoriosVacios: string[] = [];

    const filters = filtros.map((field) => {
      let values = dataFiltered[field.equivalence];

      if (!Array.isArray(values)) {
        values = [values];
      }

      if (field.type === 'dxDateRangeBox') {
        // Un extremo vacio pasaba por 'new Date(null)', que es el 1 de enero de 1970: se
        // consultaba un rango imposible y el tablero mostraba 0 filas sin avisar de nada.
        const extremos = [values[0], values[1]]
          .map((valor) => (valor === null || valor === undefined || valor === '' ? null : new Date(valor)))
          .filter((fecha): fecha is Date => fecha !== null && !isNaN(fecha.getTime()));

        if (extremos.length === 2) {
          const [desde, hasta] = extremos;
          desde.setHours(0, 0, 0, 0);
          hasta.setHours(23, 59, 59, 999);
          values = [desde, hasta];
        } else {
          // Con una sola fecha no hay rango que consultar: se avisa y no se ejecuta.
          // Sin ninguna, el filtro simplemente no aplica.
          if (extremos.length === 1) {
            rangosIncompletos.push(field.label);
          }
          values = [];
        }
      }

      const valores = values.filter((value: any) => value !== null && value !== undefined && value !== '');

      // El RequiredRule del formulario no atrapa un rango de fechas sin diligenciar: su valor
      // es [null, null], un arreglo de largo 2, y para DevExtreme eso ya esta lleno. Sin esta
      // verificacion un filtro obligatorio vacio se descartaria y el tablero traeria la tabla
      // completa, que es peor que las 0 filas que devolvia antes.
      if (field.required && valores.length === 0 && !rangosIncompletos.includes(field.label)) {
        obligatoriosVacios.push(field.label);
      }

      return {
        Field: field.equivalence,
        Operator: field.typeCondition || '=',
        Values: valores,
      };
    });

    if (rangosIncompletos.length > 0) {
      newToast('WARNING', `Seleccione la fecha inicial y la final en: ${rangosIncompletos.join(', ')}`, 5);
      return;
    }

    if (obligatoriosVacios.length > 0) {
      newToast('WARNING', `Debe diligenciar los filtros obligatorios: ${obligatoriosVacios.join(', ')}`, 5);
      return;
    }

    setFiltersApplied(filters);
    fetchData(filters);
  }, [filtros, board?.Rp_Query, dataFiltered]);

  const fetchData = useCallback(
    (filters) => {
      if (!filters || !board?.Rp_Query) return;

      setSending(true);
      runBoardReport(filters, board.Rp_Query)
        .then((res) => {
          setFilteredData(res);
        })
        .finally(() => {
          setFiltrosVisible(false);
          setSending(false);
        });
    },
    [board?.Rp_Query, runBoardReport]
  );

  const handleClicRefresh = useCallback(() => {
    fetchData(filtersApplied);
  }, [filtersApplied, fetchData]);

  const handleVisibleFilter = useCallback(() => {
    setFiltrosVisible(false);
  }, []);

  // Función para validar si un valor es numérico (solo dígitos)
  const isNumericValue = (value: any): boolean => {
    if (value === null || value === undefined || value === '') return false;
    const stringValue = String(value).trim();
    // Validar que solo contenga dígitos (puede tener ceros al inicio como "0701")
    return /^\d+$/.test(stringValue);
  };

  // Función para verificar si una columna es "Orden"
  const isOrderColumn = (columnName: string): boolean => {
    if (!columnName) return false;
    const normalizedName = columnName.trim().toUpperCase();
    return normalizedName === 'ORDEN';
  };

  // Handler para buscar orden al hacer clic
  const handleSearchOrder = useCallback(
    (orderNumber: string) => {
      if (!orderNumber || loadingOrder) return;
      setLoadingOrder(true);
      // Minimizar el modal si está en modo popup
      if (onMinimize) {
        onMinimize();
      }
      findOrder(orderNumber, undefined, setLoadingOrder, true);
    },
    [findOrder, loadingOrder, onMinimize]
  );

  // Función para renderizar celdas con enlaces en columnas de orden
  const onCellPrepared = useCallback(
    (e: any) => {
      if (e.rowType === 'data' && e.column) {
        const columnName = e.column.caption || e.column.dataField || '';
        const value = e.value;

        // Verificar si es columna "Orden" y el valor es numérico
        if (isOrderColumn(columnName) && isNumericValue(value)) {
          e.cellElement.innerHTML = '';
          e.cellElement.style.padding = '0';

          const link = document.createElement('a');
          link.href = '#';
          link.className = 'order-link';
          link.textContent = String(value);
          link.style.color = '#1890ff';
          link.style.textDecoration = 'none';
          link.style.cursor = loadingOrder ? 'wait' : 'pointer';
          link.style.display = 'block';
          link.style.padding = '7px 8px';

          link.addEventListener('mouseenter', () => {
            if (!loadingOrder) link.style.textDecoration = 'underline';
          });

          link.addEventListener('mouseleave', () => {
            link.style.textDecoration = 'none';
          });

          link.addEventListener('click', (event) => {
            event.preventDefault();
            handleSearchOrder(String(value));
          });

          e.cellElement.appendChild(link);
        }
      }
    },
    [handleSearchOrder, loadingOrder]
  );

  const onExporting = useCallback(
    (e) => {
      // exceljs y el exportador se cargan solo al exportar: este componente es eager (entra
      // por el barrel de componentes) y esas librerías no deben viajar en el bundle inicial.
      Promise.all([import('exceljs'), import('file-saver-es'), import('devextreme/excel_exporter')]).then(([{ Workbook }, { saveAs }, { exportDataGrid }]) => {
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet('Main sheet');
        exportDataGrid({
          component: e.component,
          worksheet,
          autoFilterEnabled: true,
        }).then(() => {
          workbook.xlsx.writeBuffer().then((buffer) => {
            saveAs(new Blob([buffer], { type: 'application/octet-stream' }), `INFORME - ${board?.Rp_Nombre || 'Tablero'}.xlsx`);
          });
        });
      });
    },
    [board]
  );

  if (!board) {
    return (
      <div className='board-viewer-empty'>
        <i className='dx-icon dx-icon-detailslayout'></i>
        <h3>No hay tablero seleccionado</h3>
        <p>Selecciona un tablero de control para comenzar a visualizar datos</p>
      </div>
    );
  }
  return (
    <div className='board-viewer-container'>
      <DataGrid
        height={height}
        allowColumnResizing={true}
        columnResizingMode='nextColumn'
        className='board-viewer-grid'
        ref={gridRef}
        onExporting={onExporting}
        dataSource={filteredData}
        rowAlternationEnabled={true}
        onCellPrepared={onCellPrepared}
        onContentReady={(e) => {
          setVisibleCount(e.component.totalCount());
        }}>
        <LoadPanelDg enabled={true} />
        <Export enabled={true} />
        <ColumnChooser enabled={true} mode='select' />
        <FilterRow visible={true} applyFilter='auto' />
        <GroupPanel visible={true} />
        <Scrolling mode='standard' columnRenderingMode='virtual' />
        <SearchPanel visible placeholder='Buscar' />
        <Paging enabled={true} />
        <Pager visible={true} allowedPageSizes={[20, 50, 100, 'all']} displayMode='full' showPageSizeSelector={true} showInfo={true} showNavigationButtons={true} />
        <Toolbar>
          <Item location='before' showText='always'>
            <div className='grid-header'>
              {board.Rp_Nombre} {gridRef.current?.instance ? `(${gridRef.current.instance.totalCount()} filas)` : ''}
              {loadingOrder && (
                <span style={{ marginLeft: '10px' }}>
                  <LoadIndicator visible={true} width={20} height={20} />
                </span>
              )}
            </div>
          </Item>

          {showCloseButton && (
            <Item location='after' locateInMenu='auto' showText='inMenu' widget='dxButton'>
              <Button icon='close' text='Cerrar' stylingMode='text' onClick={onClose} />
            </Item>
          )}

          <Item location='after' locateInMenu='auto' showText='inMenu' widget='dxButton'>
            <Tooltip target='.filter-button-board' showEvent='mouseenter' hideEvent='mouseleave' position='top'>
              Filtrar
            </Tooltip>
            <Button className='filter-button-board' icon={filter} text='Filtrar' stylingMode='text' onClick={() => setFiltrosVisible(true)} />
          </Item>

          <Item location='before' name='groupPanel' />

          <Item location='after' locateInMenu='auto' showText='inMenu' widget='dxButton'>
            <Button id='refreshRpBoard' onClick={handleClicRefresh} icon='refresh' text='Refrescar' stylingMode='text' />
            <Tooltip target={'#refreshRpBoard'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
              Refrescar datos
            </Tooltip>
          </Item>

          <Item location='after' name='exportButton' />
          <Item location='after' name='columnChooserButton' />
          <Item location='after' name='searchPanel' />
        </Toolbar>
      </DataGrid>

      {/* Más ancho que el de por defecto (500): los filtros de los tableros son listas de
          contratos, EPS y estudios, cuyos nombres no caben en el ancho normal. */}
      <FormPopupSimpleItem width={640} dataSource={dataFiltered} isSaveDisabled={false} loading={sending} onSave={handleClicFilter} title='Seleccione los filtros' visible={filtrosVisible} setVisible={handleVisibleFilter} fields={filtros} />
    </div>
  );
};